import {useState} from 'react'
import {useNavigate} from 'react-router'
import isEqual from 'lodash/isEqual'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Container} from '@freckle/educator-materials/ts/components/container'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {fetchSummary} from '@freckle/school/ts/common/models/summary'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {calculateUnmanagedStudents} from '@freckle/school/ts/rostering/components/clean-up-roster/clean-up-phase'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {
  removalConfirmation,
  explanationWrapper,
  subTitleStyle,
  containerStyle,
  returnButton,
} from './remove-self-rostered-teachers.module.scss'

type WrapperProps = {
  removedAffiliations: number
  schools: Array<SchoolAttrs>
  schoolIds: Array<number>
  schoolAdmin: SchoolAdminAttrs
}

export function RemoveSelfRosteredTeachersConfirmationWrapper(props: WrapperProps) {
  const {schoolIds, schoolAdmin} = props
  const summary = useAsync(() => fetchSummary(schoolAdmin.id, schoolIds), 'fetchSummary', [
    schoolAdmin.id,
    schoolIds,
  ])
  return (
    <WithResources
      resources={{summary}}
      render={({summary}) => (
        <RemoveSelfRosteredTeachersConfirmation {...props} summary={summary} />
      )}
    />
  )
}

type Props = {
  removedAffiliations: number
  schools: Array<SchoolAttrs>
  summary: SummaryT
}

function RemoveSelfRosteredTeachersConfirmation(props: Props) {
  const {removedAffiliations, schools, summary} = props
  const routerHistory = useNavigate()
  const [unmanagedStudents, setUnmanagedStudents] = useState(0)
  const [identityProviders, setIdentityProviders] = useState<Array<IdentityProviderT>>([])
  const {t} = useSafeTranslation()

  useSafeEffectExtraDeps<{schools: SchoolAttrs[]; summary: SummaryT}>(
    ({schools, summary}) => {
      const {numUnmanagedStudents, identityProviders} = calculateUnmanagedStudents(schools, summary)
      setUnmanagedStudents(numUnmanagedStudents)
      setIdentityProviders(identityProviders)
    },
    [],
    {
      schools: {value: schools, comparator: isEqual},
      summary: {value: summary, comparator: isEqual},
    },
  )

  const idp =
    identityProviders.length === 1
      ? t('DISPLAY_IDP', {idp: identityProviders[0]})
      : t('REMOVE_REOSTERED_TEACHERS_PLURAL_IDP_TEXT')

  const renderUnmanagedStudentsExplanation =
    unmanagedStudents === 0 ? (
      <p>{t('REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_NO_STUDENTS', {idp})}</p>
    ) : (
      <div className={explanationWrapper}>
        <Flex flexDirection="column">
          <SafeTrans
            i18nKey="REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_RESULTS"
            values={{numUnmanagedStudents: unmanagedStudents, idp}}
            components={{
              text: <Text style="body-2" />,
            }}
          />
          <Container size="xs" className={containerStyle}>
            <SafeTrans
              i18nKey="REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_OPTION_SAVE_DATA"
              values={{idp}}
              components={{
                text: <Text style="body-2" />,
                label: <Label variant="label-2" className={subTitleStyle} />,
                subtitle: <Text style="subtitle-2" />,
              }}
            />
          </Container>
          <Container size="xs" className={containerStyle}>
            <SafeTrans
              i18nKey="REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_OPTION_REMOVE_STUDENTS"
              values={{idp}}
              components={{
                text: <Text style="body-2" />,
                label: <Label variant="label-2" className={subTitleStyle} />,
                subtitle: <Text style="subtitle-2" />,
                cleanupLink: <Link href={Routes.rostering.cleanUpRosterLink()} reactRouter />,
              }}
            />
          </Container>
        </Flex>
      </div>
    )

  const returnToDashboard = () => routerHistory(Routes.dashboard())

  return (
    <>
      <div className={removalConfirmation}>
        <Text style="subtitle-2">
          {t('REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_TEACHER_RESULTS', {
            numRemovedAffiliations: removedAffiliations,
          })}
        </Text>
      </div>
      <Text style="subtitle-2">
        <SafeTrans
          i18nKey="REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_STUDENT_EXPLANATION_DESCRIPTION"
          values={{idp}}
        />
      </Text>
      {renderUnmanagedStudentsExplanation}
      <Button
        onClick={returnToDashboard}
        dataTest="cancel-remove-self-rostered-teachers-btn"
        className={returnButton}
      >
        {t('REMOVE_SELF_ROSTERED_TEACHERS_CONFIRMATION_GO_BACK')}
      </Button>
    </>
  )
}
